import { useMemo } from 'react'

import { useCurrentOrgId } from '~/store/slices/currentOrg'

import type { VehicleModel } from '../../client/fleet-management'
import { type PollingOptions, usePolling } from '../polling'
import useManagedOrgTree from './organizations'
import useDataManager from './useDataManager'
import { EMPTY, useNormalizedFilters } from './utils'

type VehicleFilters = {
  orgId?: string
  siteId?: string
}

type UseManagedVehiclesInput = {
  filters?: VehicleFilters
  options?: PollingOptions
  /**
   * When false (default), logically-deleted vehicles are excluded so live surfaces
   * (vehicles page, map, dropdowns, detail page) never show them. Name-resolution
   * consumers (VehicleLink, auth-tag maps) opt in with true.
   */
  includeDeleted?: boolean
}

export default function useManagedVehicles({ filters, options, includeDeleted }: UseManagedVehiclesInput = {}) {
  const { vehicles } = useDataManager()
  const { data, ...rest } = vehicles

  usePolling(vehicles, options)

  return {
    ...rest,
    vehicles: useFilteredVehicles(data, filters, includeDeleted)
  }
}

function useFilteredVehicles(data: VehicleModel[], filters?: VehicleFilters, includeDeleted = false) {
  const currentOrgId = useCurrentOrgId()
  const { orgTree } = useManagedOrgTree()

  const normalizedFilters = useNormalizedFilters(filters, {
    orgId: currentOrgId || undefined
  })

  return useMemo(() => {
    const { orgId, siteId } = normalizedFilters

    if (siteId === EMPTY) {
      return []
    }

    let filteredVehicles = data

    // The store holds all vehicles including logically-deleted ones (loaded once for
    // historical name resolution). Exclude them by default here so only name-resolution
    // consumers that explicitly opt in ever see deleted vehicles.
    if (!includeDeleted) {
      filteredVehicles = filteredVehicles.filter((vehicle) => !vehicle.logicalDeleteIn)
    }

    if (orgId != null) {
      const descendants = orgTree.indexes.descendantIds.get(orgId)
      filteredVehicles = filteredVehicles.filter(
        (vehicle) => vehicle.organizationId === orgId || descendants?.has(vehicle.organizationId)
      )
    }

    if (siteId != null) {
      filteredVehicles = filteredVehicles.filter((vehicle) => vehicle.homeSiteId === siteId)
    }

    return filteredVehicles
  }, [data, normalizedFilters, orgTree, includeDeleted])
}
